/**
 * Voyado Elevate: product card renderer.
 *
 * render()/renderAll() render the full card client-side, from Elevate's own
 * data. Nothing currently calls them: the search page, collection pages and
 * recommendation carousels have each since moved to a native
 * snippets/product-card.liquid render instead (assets/elevate-native-cards.js),
 * for SAMI Product Labels and native member-price compatibility. They stay
 * for now rather than being deleted alongside that change, so the switch to
 * renderSkeleton()/renderSkeletonAll() below stays a small, easily-reverted
 * diff on its own - see assets/elevate-list.js.
 *
 * Exposed as `window.CGElevateCard`. Requires elevate-core.js.
 *
 * Member price is patched in separately, after render(), by
 * assets/elevate-member-price.js — Elevate's `prices` (customPrices) stays
 * empty because the Shopify app does not sync variant metafields, so this
 * file only carries `data-variant-key` on the card for that script to read.
 *
 * STILL NOT RENDERED, and deliberately so: campaign badges beyond Elevate's
 * own discount badge, and the two stock status rows (online stock and
 * click-and-collect). Both need catalog data that is currently absent:
 * `custom` is empty on every product and variant, and `availability` only
 * ever reports `channel: "ONLINE"`. Once Voyado confirms how `storeStock` and
 * `customLabels` get populated, they slot into `stockStatus()` below. We do
 * not guess attribute names before then, because guessing means rewriting.
 *
 * The Liquid card at snippets/product-card.liquid:475-530 is the reference for
 * that markup: `.stock-status-item`, `.status-circle--available|lowstock|
 * unavailable`, `.product-cart--stock-status-text`, and `.price__member` /
 * `.price-item--member` from snippets/price.liquid.
 *
 * See docs/voyado-elevate-implementering.md sections 2 and 3.
 */

(function () {
  'use strict';

  const E = window.CGElevate;

  /**
   * Image display is an Elevate setting, not a theme choice. `effect` is one of
   * NONE, SWAP (hover swaps to the second image) or GALLERY (multiple images).
   * We honour SWAP because it is free, and treat GALLERY as NONE for now since
   * nothing on CG uses it yet.
   */
  function image(product, index) {
    const info = product.imageInfo || {};
    const images = info.images || [];
    const first = images[0] && images[0].sources && images[0].sources[0];
    const url = E.safeUrl(first && first.url);
    const alt = (images[0] && images[0].alt) || product.title || '';

    if (!url) {
      return '<span class="elevate-card__img elevate-card__img--placeholder"></span>';
    }

    const swapSource = info.effect === 'SWAP' && images[1] && images[1].sources && images[1].sources[0];
    const swapUrl = E.safeUrl(swapSource && swapSource.url);

    // Only the first few cards are worth loading eagerly.
    const loading = index < 4 ? 'eager' : 'lazy';
    const priority = index === 0 ? ' fetchpriority="high"' : '';

    let html =
      '<img class="elevate-card__img" src="' + E.esc(url) + '" alt="' + E.esc(alt) +
      '" loading="' + loading + '" decoding="async"' + priority + '>';

    if (swapUrl) {
      html +=
        '<img class="elevate-card__img elevate-card__img--hover" src="' + E.esc(swapUrl) +
        '" alt="" loading="lazy" decoding="async" aria-hidden="true">';
    }

    return html;
  }

  /**
   * Discount percentage is computed here rather than taken from Elevate's own
   * DISCOUNT badge, so the number always matches the prices we render. Elevate's
   * DISCOUNT badge is then skipped to avoid showing it twice.
   */
  function discountPercent(product) {
    const sp = product.sellingPrice && product.sellingPrice.min;
    const lp = product.listPrice && product.listPrice.min;
    if (sp === null || sp === undefined || lp === null || lp === undefined) return 0;
    if (Number(lp) <= Number(sp)) return 0;
    return Math.round((1 - Number(sp) / Number(lp)) * 100);
  }

  /**
   * Reuses the theme's own `.badge` classes rather than inventing new ones, so
   * Elevate-rendered cards inherit the existing styling from
   * assets/component-badge.css and stay visually identical to the Liquid card at
   * snippets/product-card.liquid:140-216.
   *
   * Note the label format: the Liquid card renders "- 60%" with a space, not
   * "-60%".
   */
  function badges(product) {
    const parts = [];
    const discount = discountPercent(product);

    // First, matching the order in the Liquid card. The rule lives in
    // elevate-core.js because the autocomplete panel needs it too, and this file
    // only loads on the search page.
    if (E.isMember(product)) {
      parts.push(
        '<span class="badge badge--member" aria-hidden="true">' +
          E.esc(E.t('member_badge', 'Member')) +
          '</span>'
      );
    }

    if (discount > 0) {
      parts.push('<span class="badge badge--sale" aria-hidden="true">- ' + discount + '%</span>');
    }

    const groups = product.badges || {};
    let hasSoldOut = false;

    (groups.primary || []).concat(groups.secondary || []).forEach((badge) => {
      if (!badge || !badge.label) return;
      // Skip Elevate's own discount badge, we render our own above so the number
      // always matches the prices we display.
      if (badge.theme === 'DISCOUNT') return;
      if (/^utsolgt$|^sold out$/i.test(badge.label)) hasSoldOut = true;
      // Elevate's own theme name is carried through as a data attribute rather
      // than mapped to a class here. Custom badges are configured in Experience
      // with a theme (Theme 1, Theme 2, ...), and which one means what is a
      // merchandiser decision. Styling one of them like the theme's member badge
      // is then a CSS rule in component-elevate-grid.css, with no JS change.
      parts.push(
        '<span class="badge badge--custom" data-badge-theme="' +
          E.esc(badge.theme || '') +
          '" aria-hidden="true">' +
          E.esc(badge.label) +
          '</span>'
      );
    });

    if (!product.inStock && !hasSoldOut) {
      parts.push('<span class="badge badge--soldout" aria-hidden="true">' + E.esc(E.t('sold_out', 'Sold out')) + '</span>');
    }

    if (!parts.length) return '';
    return '<div class="elevate-card__badges">' + parts.join('') + '</div>';
  }

  /**
   * Wishlist button. The Wishlist Guru app binds to `.th_wl_col_btn` and reads
   * the two data attributes, exactly as snippets/product-card.liquid:76-80 does.
   *
   * Elevate gives us the product key but not a variant id, so we pass the product
   * key for both. Verify against the app before relying on variant-level
   * wishlisting.
   *
   * Note: the app embed is currently absent from the staging theme, so this
   * renders an inert div there. It should light up in production.
   */
  function wishlist(product) {
    if (!product.key) return '';
    const key = E.esc(product.key);
    return '<div class="th_wl_col_btn" data-product_id="' + key + '" data-variant_id="' + key + '"></div>';
  }

  function price(product) {
    const selling = E.priceRange(product.sellingPrice);
    if (!selling) return '';

    const lp = product.listPrice || {};
    const sp = product.sellingPrice && product.sellingPrice.min;
    const onSale =
      lp.min !== null && lp.min !== undefined && sp !== null && sp !== undefined && Number(lp.min) > Number(sp);

    let html = '<div class="elevate-card__price' + (onSale ? ' elevate-card__price--sale' : '') + '">';
    html += '<span class="elevate-card__price-current">' + E.esc(selling) + '</span>';
    if (onSale) {
      html += '<s class="elevate-card__price-was">' + E.esc(E.money(lp.min)) + '</s>';
    }
    html += '</div>';
    return html;
  }

  /**
   * Renders one card. `group` is a productGroup from the response; `index` is
   * the position in the grid and only affects image loading priority.
   *
   * The ticket is read at group level first, then falls back to the product.
   * It is carried on the link as `data-voyado-ticket` so click handlers can pass
   * it back to Elevate in the notification.
   */
  function render(group, index) {
    const product = (group.products && group.products[0]) || group;
    const ticket = group.ticket || product.ticket || '';
    const url = E.safeUrl(product.link);
    // The first variant only: cards render one price, same as the Liquid
    // card's `selected_or_first_available_variant`. See
    // assets/elevate-member-price.js for why this key doubles as a SKU.
    const variantKey = (product.variants && product.variants[0] && product.variants[0].key) || '';

    return (
      '<li class="elevate-card" data-product-key="' + E.esc(product.key || '') +
      '" data-variant-key="' + E.esc(variantKey) + '">' +
      wishlist(product) +
      '<a class="elevate-card__link" href="' + E.esc(url) + '" data-voyado-ticket="' + E.esc(ticket) + '">' +
      '<span class="elevate-card__media">' + image(product, index) + badges(product) + '</span>' +
      '<span class="elevate-card__body">' +
      // Always rendered, even with no brand: the empty line still has to be
      // reserved, or a product without a vendor pulls its title and price up out
      // of line with the cards beside it. Same as the Liquid card, which always
      // emits the brand span.
      '<span class="elevate-card__brand">' + E.esc(product.brand || '') + '</span>' +
      '<span class="elevate-card__title">' + E.esc(E.titleWithoutBrand(product)) + '</span>' +
      price(product) +
      '</span>' +
      '</a>' +
      '</li>'
    );
  }

  /**
   * Renders a full grid and returns the HTML. Callers append rather than replace
   * when paginating.
   */
  function renderAll(groups, startIndex) {
    const offset = startIndex || 0;
    return (groups || []).map((group, i) => render(group, offset + i)).join('');
  }

  /**
   * A skeleton placeholder, for grids that hand off to
   * assets/elevate-native-cards.js's upgradeLazily(). Carries a real link and
   * ticket - both required by that swap - but no image/title/price, so the
   * swap reads as a card finishing loading rather than one real card turning
   * into a different one. See that file's own comment for why a PLP/search
   * grid does not fetch native cards for every product up front the way
   * render()/renderAll() above do for recommendation carousels.
   *
   * Reuses .elevate-card__brand/__title/__price empty, rather than a
   * separate skeleton markup - see the "Skeleton" comment in
   * component-elevate-grid.css for why that's what actually keeps this the
   * same size as the native card it becomes.
   */
  function renderSkeleton(group) {
    const product = (group.products && group.products[0]) || group;
    const ticket = group.ticket || product.ticket || '';
    const url = E.safeUrl(product.link);

    return (
      '<li class="elevate-card elevate-card--skeleton" data-product-key="' + E.esc(product.key || '') + '">' +
      '<a class="elevate-card__link" href="' + E.esc(url) + '" data-voyado-ticket="' + E.esc(ticket) + '">' +
      '<span class="elevate-card__media"></span>' +
      '<span class="elevate-card__body">' +
      '<span class="elevate-card__brand"></span>' +
      '<span class="elevate-card__title"></span>' +
      '<span class="elevate-card__price"></span>' +
      '<span class="elevate-card__stock"></span>' +
      '</span>' +
      '</a>' +
      '</li>'
    );
  }

  function renderSkeletonAll(groups) {
    return (groups || []).map(renderSkeleton).join('');
  }

  /**
   * Delegated click handling for a grid container. Sends the click notification
   * without delaying navigation, because the SDK sends with keepalive.
   */
  function bindClicks(container) {
    container.addEventListener('click', (event) => {
      const link = event.target.closest('[data-voyado-ticket]');
      if (!link) return;
      E.notifyClick(link.dataset.voyadoTicket);
    });
  }

  window.CGElevateCard = {
    render: render,
    renderAll: renderAll,
    renderSkeleton: renderSkeleton,
    renderSkeletonAll: renderSkeletonAll,
    bindClicks: bindClicks,
    discountPercent: discountPercent
  };
})();
